import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import './UserPointsPage.css';

const UserPointsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pointsSummary, setPointsSummary] = useState(null);
  const [selectedReaderForHistory, setSelectedReaderForHistory] = useState(null);
  const [readerPointsHistory, setReaderPointsHistory] = useState(null);
  const [pointsHistoryPagination, setPointsHistoryPagination] = useState({});
  const [pointsFilters, setPointsFilters] = useState({
    reason: '',
    startDate: '',
    endDate: ''
  });
  const [currentPointsHistoryPage, setCurrentPointsHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadPointsSummary();
  }, []);

  useEffect(() => {
    if (selectedReaderForHistory) {
      loadReaderPointsHistory();
    }
  }, [selectedReaderForHistory, currentPointsHistoryPage, pointsFilters]);

  const loadPointsSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/me/points-summary');
      setPointsSummary(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load points summary:', err);
      setError('Failed to load points. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadReaderPointsHistory = async () => {
    try {
      setHistoryLoading(true);
      const params = {
        page: currentPointsHistoryPage,
        limit: 50,
        ...pointsFilters
      };
      
      const response = await api.get(`/users/me/readers/${selectedReaderForHistory.id}/points-history`, { params });
      setReaderPointsHistory(response.data);
      setPointsHistoryPagination(response.data.pagination);
      setError(null);
    } catch (err) {
      console.error('Failed to load reader points history:', err);
      setError('Failed to load points history. Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatReason = (reason) => {
    return reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="user-points-page loading">
        <LoadingSpinner size="large" message="Loading points..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-points-page error">
        <div className="error-message">
          <h2>Unable to load points</h2>
          <p>{error}</p>
          <button onClick={loadPointsSummary} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-points-page">
      <div className="page-header">
        <h1>Points</h1>
        <p>See how you've earned points</p>
      </div>

      {pointsSummary && (
        <div className="points-overview">
          {/* Readers Summary */}
          <div className="readers-summary">
            <h2>Your readers ({pointsSummary.user.readers?.length || 0})</h2>
            {pointsSummary.user.readers?.length > 0 ? (
              <div className="readers-grid">
                {pointsSummary.user.readers.map(reader => (
                  <div key={reader.id} className="reader-summary-card">
                    <div className="reader-header">
                      <h3>
                        {reader.name}
                        {reader.isPrimary && <span className="primary-badge">Primary</span>}
                      </h3>
                      <button 
                        onClick={() => {
                          setSelectedReaderForHistory(reader);
                          setCurrentPointsHistoryPage(1);
                        }}
                        className="view-history-btn"
                      >
                        View history
                      </button>
                    </div>
                    
                    {/* Points Information */}
                    {reader.points ? (
                      <div className="points-info">
                        <div className="points-stats">
                          <div className="stat">
                            <span className="label">Total points</span>
                            <span className="value">{reader.points.totalPoints.toLocaleString()}</span>
                          </div>
                          <div className="stat">
                            <span className="label">Current level</span>
                            <span className="value">{reader.points.currentLevel}</span>
                          </div>
                          <div className="stat">
                            <span className="label">Lifetime points</span>
                            <span className="value">{reader.points.lifetimePoints.toLocaleString()}</span>
                          </div>
                          <div className="stat">
                            <span className="label">To next level</span>
                            <span className="value">{reader.points.pointsToNextLevel}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-points">
                        <p>No points yet.</p>
                      </div>
                    )}

                    {/* Bible Completions */}
                    {reader.completions?.length > 0 && (
                      <div className="completions-info">
                        <h4>Reading progress</h4>
                        {reader.completions.map(completion => (
                          <div key={completion.completionNumber} className="completion-item">
                            <span className="completion-number">Cycle {completion.completionNumber}</span>
                            <div className="completion-progress">
                              <span>{completion.chaptersRead}/1189 chapters</span>
                              <span className="completion-percentage">
                                ({Math.round((completion.chaptersRead / 1189) * 100)}%)
                              </span>
                            </div>
                            <span className="completion-points">{completion.totalPoints} points</span>
                            {completion.completedAt && (
                              <span className="completed-badge">✓ Completed</span>
                            )}
                            {completion.isActive && <span className="active-badge">Active</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recent Badges */}
                    {reader.earnedBadges?.length > 0 && (
                      <div className="badges-info">
                        <h4>Recent badges ({reader.earnedBadges.length})</h4>
                        <div className="badges-list">
                          {reader.earnedBadges.slice(0, 3).map(badgeEarned => (
                            <div key={badgeEarned.earnedAt} className="badge-item">
                              <span className={`badge-name ${badgeEarned.badge.rarity}`}>
                                {badgeEarned.badge.name}
                              </span>
                              <span className="badge-points">+{badgeEarned.pointsAwarded} pts</span>
                              <span className="badge-date">
                                {new Date(badgeEarned.earnedAt).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-readers">
                <p>No active readers found. Create a reader to start tracking your progress!</p>
              </div>
            )}
          </div>

          {/* Points Breakdown */}
          {pointsSummary.pointsStats?.length > 0 && (
            <div className="points-breakdown">
              <h2>How you've earned points</h2>
              <div className="breakdown-table">
                <table>
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Times</th>
                      <th>Total points</th>
                      <th>Avg points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pointsSummary.pointsStats.map(stat => (
                      <tr key={stat.reason}>
                        <td>
                          <span className={`reason-badge reason-${stat.reason}`}>
                            {formatReason(stat.reason)}
                          </span>
                        </td>
                        <td>{stat.count}</td>
                        <td className="points-cell">
                          <span className="points positive">+{stat.totalPoints}</span>
                        </td>
                        <td>{stat.averagePoints}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {pointsSummary.recentTransactions?.length > 0 && (
            <div className="recent-activity">
              <h2>Recent activity</h2>
              <div className="activity-list">
                {pointsSummary.recentTransactions.slice(0, 10).map(transaction => (
                  <div key={transaction.id} className="activity-item">
                    <div className="activity-info">
                      <span className="activity-reader">{transaction.reader?.name}</span>
                      <span className="activity-description">{transaction.description}</span>
                    </div>
                    <div className="activity-details">
                      <span className={`points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                        {transaction.points > 0 ? '+' : ''}{transaction.points}
                      </span>
                      <span className="activity-date">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reader Points History Modal */}
      {selectedReaderForHistory && (
        <div className="modal-overlay">
          <div className="modal large-modal">
            <div className="modal-header">
              <h3>Points history: {selectedReaderForHistory.name}</h3>
              <button 
                onClick={() => {
                  setSelectedReaderForHistory(null);
                  setReaderPointsHistory(null);
                }}
                className="modal-close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {historyLoading && <LoadingSpinner message="Loading points history..." />}
              
              {readerPointsHistory && (
                <>
                  {/* Summary Statistics */}
                  {readerPointsHistory.summary?.length > 0 && (
                    <div className="points-summary-section">
                      <h4>Points summary by activity</h4>
                      <div className="summary-grid">
                        {readerPointsHistory.summary.map(stat => (
                          <div key={stat.reason} className="summary-card">
                            <div className="summary-reason">{formatReason(stat.reason)}</div>
                            <div className="summary-stats">
                              <span>{stat.count} times</span>
                              <span>{stat.totalPoints} points</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div className="points-filters">
                    <div className="filter-row">
                      <div className="form-group">
                        <label>Activity type</label>
                        <select
                          value={pointsFilters.reason}
                          onChange={(e) => setPointsFilters(prev => ({ ...prev, reason: e.target.value }))}
                        >
                          <option value="">All activities</option>
                          <option value="chapter_read_completion">Chapter read (first time)</option>
                          <option value="chapter_read_repeat">Chapter read (repeat)</option>
                          <option value="bible_completed">Bible completed</option>
                          <option value="badge_earned">Badge earned</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>From date</label>
                        <input
                          type="date"
                          value={pointsFilters.startDate}
                          onChange={(e) => setPointsFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                      </div>
                      <div className="form-group">
                        <label>To date</label>
                        <input
                          type="date"
                          value={pointsFilters.endDate}
                          onChange={(e) => setPointsFilters(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Transactions Table */}
                  <div className="transactions-history">
                    <h4>Transaction history</h4>
                    {readerPointsHistory.transactions?.length > 0 ? (
                      <>
                        {/* Desktop Table View */}
                        <div className="transactions-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Points</th>
                                <th>Activity</th>
                                <th>Description</th>
                                <th>Multiplier</th>
                              </tr>
                            </thead>
                            <tbody>
                              {readerPointsHistory.transactions.map(transaction => (
                                <tr key={transaction.id}>
                                  <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                                  <td>
                                    <span className={`points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                                      {transaction.points > 0 ? '+' : ''}{transaction.points}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`reason-badge reason-${transaction.reason}`}>
                                      {formatReason(transaction.reason)}
                                    </span>
                                  </td>
                                  <td className="description-cell">{transaction.description}</td>
                                  <td>{transaction.multiplier}x</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="transactions-card-container">
                          {readerPointsHistory.transactions.map(transaction => (
                            <div key={transaction.id} className="transaction-card">
                              <div className="transaction-card-header">
                                <div className="transaction-card-date">
                                  {new Date(transaction.createdAt).toLocaleDateString()}
                                </div>
                                <div className={`transaction-card-points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                                  {transaction.points > 0 ? '+' : ''}{transaction.points}
                                </div>
                              </div>

                              <div className="transaction-card-details">
                                <div className="transaction-card-detail">
                                  <div className="transaction-card-detail-label">Activity</div>
                                  <div className="transaction-card-detail-value">
                                    <span className={`reason-badge reason-${transaction.reason}`}>
                                      {formatReason(transaction.reason)}
                                    </span>
                                  </div>
                                </div>
                                <div className="transaction-card-detail">
                                  <div className="transaction-card-detail-label">Multiplier</div>
                                  <div className="transaction-card-detail-value">{transaction.multiplier}x</div>
                                </div>
                              </div>

                              <div className="transaction-card-description">
                                {transaction.description}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Pagination */}
                        {pointsHistoryPagination.totalPages > 1 && (
                          <div className="pagination">
                            <button 
                              onClick={() => setCurrentPointsHistoryPage(prev => Math.max(1, prev - 1))}
                              disabled={!pointsHistoryPagination.hasPrevPage}
                              className="pagination-btn"
                            >
                              Previous
                            </button>
                            <span className="pagination-info">
                              Page {pointsHistoryPagination.currentPage} of {pointsHistoryPagination.totalPages}
                            </span>
                            <button 
                              onClick={() => setCurrentPointsHistoryPage(prev => prev + 1)}
                              disabled={!pointsHistoryPagination.hasNextPage}
                              className="pagination-btn"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="no-transactions">
                        <p>No transactions found for the selected filters.</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPointsPage;
